import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { Clipboard, Eye, Paperclip, PencilSquare, X } from 'react-bootstrap-icons'

export type CodeHtmlApplyPayload = {
  prompt: string
  html: string
  files: File[]
}

export type CodeHtmlEditorProps = {
  value?: string
  onChange?: (value: string) => void
  onApply?: (payload: CodeHtmlApplyPayload) => Promise<string | void> | string | void
  isApplying?: boolean
  promptPlaceholder?: string
  htmlPlaceholder?: string
  applyLabel?: string
}

type EditorAttachment = {
  id: string
  name: string
  file: File
  size: number
  type: string
}

type TabMode = 'html' | 'preview'

const DEFAULT_PROMPT =
  'Prompt a code change for this HTML field. Attach references if needed.'

const toPrettySize = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB'
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const CodeHtmlEditor = ({
  value = '',
  onChange,
  onApply,
  isApplying = false,
  promptPlaceholder = DEFAULT_PROMPT,
  htmlPlaceholder = '<p>Start writing HTML...</p>',
  applyLabel = 'Apply',
}: CodeHtmlEditorProps) => {
  const attachmentInputRef = useRef<HTMLInputElement | null>(null)
  const attachmentCounterRef = useRef(0)

  const [html, setHtml] = useState(value)
  const [prompt, setPrompt] = useState('')
  const [activeTab, setActiveTab] = useState<TabMode>('html')
  const [attachments, setAttachments] = useState<EditorAttachment[]>([])
  const [error, setError] = useState('')

  useEffect(() => {
    setHtml(value)
  }, [value])

  const canApply = useMemo(
    () => !!onApply && !isApplying && (prompt.trim().length > 0 || attachments.length > 0),
    [attachments.length, isApplying, onApply, prompt]
  )

  const handleHtmlChange = useCallback(
    (nextValue: string) => {
      setHtml(nextValue)
      onChange?.(nextValue)
    },
    [onChange]
  )

  const handleAttach = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const incoming = Array.from(event.target.files || []).filter((file) => {
      if (file.type.startsWith('image/')) return true
      if (file.type === 'text/html' || file.type === 'text/plain') return true
      const loweredName = file.name.toLowerCase()
      return loweredName.endsWith('.html') || loweredName.endsWith('.htm')
    })
    event.target.value = ''
    if (incoming.length === 0) return

    const nextItems = incoming.map((file) => ({
      id: `code-attachment-${attachmentCounterRef.current++}`,
      name: file.name,
      file,
      size: file.size,
      type: file.type,
    }))

    setAttachments((previous) => [...previous, ...nextItems].slice(0, 8))
  }, [])

  const removeAttachment = useCallback((id: string) => {
    setAttachments((previous) => previous.filter((item) => item.id !== id))
  }, [])

  const runApply = useCallback(async () => {
    if (!canApply || !onApply) return

    setError('')
    try {
      const nextHtml = await onApply({
        prompt: prompt.trim(),
        html,
        files: attachments.map((item) => item.file),
      })

      if (typeof nextHtml === 'string' && nextHtml.trim()) {
        handleHtmlChange(nextHtml)
      }

      setPrompt('')
      setAttachments([])
    } catch (err: any) {
      setError(String(err?.message || 'Unable to apply code edits right now.'))
    }
  }, [attachments, canApply, handleHtmlChange, html, onApply, prompt])

  return (
    <div className="osirus-code-panel">
      <div className="osirus-code-topbar">
        <div className="osirus-code-tabs">
          <button
            type="button"
            className={activeTab === 'html' ? 'is-active' : ''}
            onClick={() => setActiveTab('html')}
          >
            <PencilSquare />
            HTML
          </button>
          <button
            type="button"
            className={activeTab === 'preview' ? 'is-active' : ''}
            onClick={() => setActiveTab('preview')}
          >
            <Eye />
            Preview
          </button>
        </div>

        <button
          type="button"
          className="osirus-code-copy"
          disabled={!html.trim()}
          onClick={() => {
            if (!html.trim()) return
            void navigator.clipboard?.writeText(html)
          }}
        >
          <Clipboard />
          Copy HTML
        </button>
      </div>

      <div className="osirus-code-stage">
        {activeTab === 'html' ? (
          <textarea
            value={html}
            onChange={(event) => handleHtmlChange(event.target.value)}
            className="osirus-code-html-input"
            placeholder={htmlPlaceholder}
            spellCheck={false}
          />
        ) : (
          <div
            className="osirus-code-preview"
            dangerouslySetInnerHTML={{ __html: html || '<p></p>' }}
          />
        )}
      </div>

      <div className="osirus-code-promptbar">
        <input
          ref={attachmentInputRef}
          type="file"
          accept="image/*,.html,.htm,text/html,text/plain"
          multiple
          className="d-none"
          onChange={handleAttach}
        />
        <button
          type="button"
          className="osirus-code-attach"
          onClick={() => attachmentInputRef.current?.click()}
        >
          <Paperclip />
          Attach
        </button>
        <input
          type="text"
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              void runApply()
            }
          }}
          className="osirus-code-prompt"
          placeholder={promptPlaceholder}
        />
        <button
          type="button"
          className="osirus-code-apply"
          disabled={!canApply}
          onClick={() => {
            void runApply()
          }}
        >
          {isApplying ? 'Applying...' : applyLabel}
        </button>
      </div>

      {attachments.length > 0 && (
        <div className="osirus-code-attachments">
          {attachments.map((attachment) => (
            <div className="osirus-code-attachment" key={attachment.id}>
              <span className="osirus-code-attachment-name" title={attachment.name}>
                {attachment.name}
              </span>
              <span className="osirus-code-attachment-size">
                {toPrettySize(attachment.size)}
              </span>
              <button
                type="button"
                className="osirus-code-attachment-remove"
                onClick={() => removeAttachment(attachment.id)}
              >
                <X />
              </button>
            </div>
          ))}
        </div>
      )}

      {error && <div className="osirus-generate-error">{error}</div>}
    </div>
  )
}

export default CodeHtmlEditor
